import { useEffect, useState } from "react";
import { ShieldCheck, Camera, Award, Repeat, Trash2 } from "lucide-react";
import { t } from "../i18n";
import { fmtDate } from "./DateField";

const TrustRow = ({ locale }) => (
  <ul className="grid gap-2 text-xs text-charcoal/80" data-testid="trust-markers">
    {[[ShieldCheck, "trustSecure"], [Camera, "trustPhoto"], [Award, "trustFlorists"], [Repeat, "trustSubstitution"]].map(
      ([Icon, key]) => (
        <li key={key} className="flex items-center gap-2">
          <Icon size={14} className="text-brass shrink-0" />
          {t(locale, key)}
        </li>
      ),
    )}
  </ul>
);

// The code is only sent to the server when Apply is pressed — typing never re-quotes.
const CouponField = ({ locale, code, setCode, codeApplied, compact, error }) => {
  const [draft, setDraft] = useState(code || "");
  useEffect(() => { setDraft(code || ""); }, [code]);
  const dirty = (draft || "").trim() !== (code || "").trim();
  const apply = () => setCode((draft || "").trim().toUpperCase());

  return (
    <div className="space-y-2">
      <label className="smallcaps" htmlFor={`discount-code-${compact ? "m" : "d"}`}>{t(locale, "couponQuestion")}</label>
      <div className="flex gap-2">
        <input
          id={`discount-code-${compact ? "m" : "d"}`}
          data-testid="discount-code"
          className="field flex-1"
          placeholder={t(locale, "couponPlaceholder")}
          value={draft}
          onChange={(e) => setDraft(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
        />
        <button type="button" data-testid="apply-code" className="btn-ghost px-4 whitespace-nowrap"
                disabled={!draft.trim() || (!dirty && codeApplied)} onClick={apply}>
          {t(locale, "applyCode")}
        </button>
      </div>
      {dirty && draft.trim() ? (
        <p className="text-xs text-muted" data-testid="discount-pending">{t(locale, "couponHint")}</p>
      ) : null}
      {!dirty && code ? (
        <p className={`text-xs ${codeApplied ? "text-success" : "text-muted"}`} data-testid="discount-status">
          {codeApplied ? error : (error || "This code needs the email address it was sent to, or it has already been used.")}
          {codeApplied ? (
            <button type="button" data-testid="discount-remove" className="ms-2 underline underline-offset-2"
                    onClick={() => { setDraft(""); setCode(""); }}>
              {t(locale, "couponRemove")}
            </button>
          ) : null}
        </p>
      ) : null}
    </div>
  );
};

export const CheckoutSummary = ({ locale, gifts, quote, code, setCode, codeApplied, recurring, planDiscount, compact,
                                  editing, onRemove }) => (
  <div className="space-y-6" data-testid="order-summary">
    <ul className="space-y-4">
      {gifts.map((g, idx) => (
        <li key={g.gift_id} className="flex gap-4" data-testid={`summary-line-${idx}`}>
          <img src={g.image} alt="" className="w-16 aspect-[4/5] object-cover rounded-sm" />
          <div className="text-xs flex-1 space-y-0.5">
            <p className="text-ink text-sm">{g.title}</p>
            <p className="text-muted">{g.variant_name} · {g.city?.name}</p>
            <p className="text-muted">{g.zone_name} · {fmtDate(g.delivery_date)}</p>
            {(g.addons || []).length ? (
              <p className="text-muted">{g.addons.map((a) => `${a.name} ×${a.qty}`).join(" · ")}</p>
            ) : null}
            {editing && onRemove ? (
              <button type="button" data-testid={`summary-remove-${idx}`} onClick={() => onRemove(g.gift_id)}
                      className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted hover:text-danger transition-colors">
                <Trash2 size={11} /> {t(locale, "removeLine")}
              </button>
            ) : null}
          </div>
          <p className="text-xs text-ink tabular whitespace-nowrap">
            {quote?.symbol} {quote?.per_delivery?.[idx]?.subtotal ?? "—"}
          </p>
        </li>
      ))}
      {gifts.length === 0 ? <li className="text-sm text-muted" data-testid="summary-empty">{t(locale, "basketEmpty")}</li> : null}
    </ul>

    <CouponField locale={locale} code={code} setCode={setCode} codeApplied={codeApplied} compact={compact}
                 error={codeApplied ? `${quote?.discount_code} · − ${quote?.symbol} ${quote?.discount}` : quote?.discount_error} />

    <dl className="text-sm space-y-2 border-t border-brass-light pt-4">
      <div className="flex justify-between"><dt>{t(locale, "subtotal")}</dt><dd className="tabular">{quote?.symbol} {quote?.subtotal ?? "—"}</dd></div>
      {quote?.addons ? <div className="flex justify-between"><dt>{t(locale, "addOns")}</dt><dd className="tabular">{quote.symbol} {quote.addons}</dd></div> : null}
      <div className="flex justify-between"><dt>{t(locale, "deliveryFee")}{gifts.length > 1 ? ` × ${gifts.length}` : ""}</dt><dd className="tabular">{quote?.symbol} {quote?.delivery_fee ?? "—"}</dd></div>
      {quote?.discount ? <div className="flex justify-between text-success"><dt>{t(locale, "discount")}</dt><dd className="tabular">− {quote.symbol} {quote.discount}</dd></div> : null}
      {quote?.plan_discount ? (
        <div className="flex justify-between text-success" data-testid="summary-plan-discount">
          <dt>{t(locale, "mySubscriptions")} −{quote.plan_discount_percent}%</dt>
          <dd className="tabular">− {quote.symbol} {quote.plan_discount}</dd>
        </div>
      ) : null}
      <div className="flex justify-between items-end border-t border-brass-light pt-4">
        <dt className="smallcaps">{t(locale, "total")}</dt>
        <dd className="font-display text-2xl text-ink tabular" data-testid="summary-total">
          {quote?.symbol} {quote?.total ?? "—"}
        </dd>
      </div>
      <p className="text-xs text-muted text-end" data-testid="summary-charge-currency">
        {t(locale, "chargedEur")}
        {quote?.reference ? ` · ≈ ${quote.reference.symbol}${quote.reference.total} ${t(locale, "todaysRate")}` : ""}
      </p>
      {recurring ? <p className="text-xs text-muted" data-testid="summary-per-delivery">{t(locale, "planPriceLocked")}</p> : null}
    </dl>

    {compact ? null : <TrustRow locale={locale} />}
  </div>
);

export default CheckoutSummary;
